/** Application chrome: */

import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { formatTime } from '@metal/shared'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export function AppShell({
  topbar,
  appbar,
  ticker,
  rail,
  railOpen,
  onRailClose,
  operator,
  children,
  bottomBar,
}: {
  topbar: ReactNode
  /** The compact top bar shown below the lg breakpoint, where the wide desktop topbar cannot fit. */
  appbar?: ReactNode
  ticker?: ReactNode
  rail?: ReactNode
  /** Below the lg breakpoint the `rail` is an off-canvas drawer rather than a fixed column. */
  railOpen?: boolean
  onRailClose?: () => void
  operator?: boolean
  bottomBar?: ReactNode
  children: ReactNode
}) {
  return (
    <div className={cx('app', operator && 'op', railOpen && 'rail-open')}>
      <a className="skip-link" href="#main">
        پرش به محتوا
      </a>
      {appbar}
      {topbar}
      {ticker}
      <div className="app-body">
        {rail}
        {rail && (
          <button type="button" className="rail-scrim" aria-label="بستن منو" onClick={onRailClose} />
        )}
        <main className="main" id="main">
          {children}
        </main>
      </div>
      {bottomBar}
    </div>
  )
}

export function Brand({
  name = 'فیدار تک',
  sub,
  to = '/',
}: {
  name?: string
  sub: string
  to?: string
}) {
  return (
    <NavLink className="brand" to={to} style={{ color: 'inherit' }}>
      <span className="brand-mark">
        <Icon name="brand-mark" />
      </span>
      <span>
        <span className="brand-name" style={{ display: 'block' }}>
          {name}
        </span>
        <span className="brand-sub">{sub}</span>
      </span>
    </NavLink>
  )
}

export function Topbar({
  variant = 'app',
  children,
  className,
}: {
  variant?: 'app' | 'public' | 'operator'
  children: ReactNode
  className?: string
}) {
  return (
    <header className={cx('topbar', className)} data-variant={variant}>
      {children}
    </header>
  )
}

export function AppBar({ children, className }: { children: ReactNode; className?: string }) {
  return <header className={cx('appbar', className)}>{children}</header>
}

export interface NavItemSpec {
  to: string
  label: string
  icon: IconName
  /** Extra paths that keep this row lit. */
  match?: string[]
}

export function Nav({ items }: { items: NavItemSpec[] }) {
  return (
    <nav className="nav">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            cx(isActive || matchesAny(it.match) ? 'is-on' : undefined)
          }
        >
          <Icon name={it.icon} />
          {it.label}
        </NavLink>
      ))}
    </nav>
  )
}

function matchesAny(patterns?: string[]): boolean {
  if (!patterns?.length) return false
  const path = window.location.pathname
  return patterns.some((p) =>
    p.endsWith('*') ? path.startsWith(p.slice(0, -1)) : path === p,
  )
}

/** The market session pill. */
export function SessionClock({
  open,
  closesAt,
  opensAt,
  short,
  className,
}: {
  open: boolean
  closesAt: Date | null
  opensAt: Date | null
  /** The operator bar uses the short form. */
  short?: boolean
  className?: string
}) {
  if (open) {
    const t = closesAt ? formatTime(closesAt) : '۱۷:۰۰'
    return (
      <span className={cx('session', className)}>
        <span className="dot dot-live pulse" />
        {short ? `بازار باز تا ${t}` : `بازار باز ــ تا ساعت ${t}`}
      </span>
    )
  }
  const t = opensAt ? formatTime(opensAt) : '۰۹:۰۰'
  return (
    <span className={cx('session is-closed', className)}>
      <Icon name="clock" size="sm" />
      {short ? `بازار بسته تا ${t}` : `بازار بسته ــ بازگشایی ${t}`}
    </span>
  )
}

/** One ticker tile, exactly as the API publishes it. */
export interface TickerItem {
  key: string
  labelFa: string
  valueFa: string
  unitFa?: string
  /** Signed percent, pre-rendered. Empty when the series has no comparison. */
  changeFa?: string
  changePercent?: number | null
  /** «روز گذشته» on an external feed. Absent on the platform's own price. */
  badgeFa?: string
  /** True only for the platform's own matched price. */
  live?: boolean
}

/** The market strip. */
export function MarketTicker({
  items,
  leadFa = 'مظنه بازار',
  stampFa,
}: {
  items: TickerItem[]
  leadFa?: string
  stampFa?: string | null
}) {
  const feeds = items.filter((i) => !i.live)
  const live = items.filter((i) => i.live)

  return (
    <div className="ticker">
      <span className="tk-lead">
        <Icon name="chart" style={{ inlineSize: 14, blockSize: 14 }} />
        {leadFa}
      </span>
      {feeds.map((it) => (
        <span className="tk-item" key={it.key}>
          <span className="tk-n">{it.labelFa}</span>
          <span className="tk-v">{it.valueFa}</span>
          {it.changeFa && (
            <span className={cx('tk-d', (it.changePercent ?? 0) >= 0 ? 'up' : 'down')}>
              {it.changeFa}
            </span>
          )}
          {it.badgeFa && <span className="tk-lag">{it.badgeFa}</span>}
        </span>
      ))}
      {live.length > 0 && <span className="tk-sep" />}
      {live.map((it) => (
        <span className="tk-item" key={it.key}>
          <span className="tk-live" />
          <span className="tk-n">{it.labelFa}</span>
          <span className="tk-v">{it.valueFa}</span>
        </span>
      ))}
      {stampFa && <span className="tk-stamp">{stampFa}</span>}
    </div>
  )
}

export function PageHeader({
  title,
  meta,
  actions,
  align = 'start',
  className,
}: {
  title: ReactNode
  meta?: ReactNode
  actions?: ReactNode
  align?: 'start' | 'end'
  className?: string
}) {
  return (
    <div className={cx('page-h', className)} style={align === 'end' ? { alignItems: 'flex-end' } : undefined}>
      <div>
        {typeof title === 'string' ? <h1 className="t-h2">{title}</h1> : title}
        {meta && <div className="row" style={{ gap: 8, marginBlockStart: 8 }}>{meta}</div>}
      </div>
      {actions && <div className="row" style={{ gap: 10 }}>{actions}</div>}
    </div>
  )
}

export function Page({
  narrow,
  wide,
  className,
  children,
  ...rest
}: {
  narrow?: boolean
  wide?: boolean
  className?: string
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cx('page', narrow && 'page-narrow', wide && 'page-wide', className)} {...rest}>
      {children}
    </div>
  )
}

export interface RailRow {
  key: string
  to: string
  label: string
  icon: IconName
  count?: number
  disabled?: boolean
}

export interface RailGroup {
  heading?: string
  rows: RailRow[]
}

export function Rail({
  groups,
  activeKey,
  className,
}: {
  groups: RailGroup[]
  activeKey?: string
  className?: string
}) {
  return (
    <aside className={cx('side', className)}>
      {groups.map((g, gi) => (
        <div key={g.heading ?? `g-${gi}`} style={{ display: 'contents' }}>
          {g.heading && <div className="side-group">{g.heading}</div>}
          {g.rows.map((r) =>
            r.disabled ? (
              <span
                key={r.key}
                className="side-row"
                aria-disabled="true"
              >
                <Icon name={r.icon} />
                {r.label}
                {r.count !== undefined && <span className="side-count">{fa(r.count)}</span>}
              </span>
            ) : (
              <NavLink
                key={r.key}
                to={r.to}
                className={activeKey === r.key ? 'is-on' : undefined}
              >
                <Icon name={r.icon} />
                {r.label}
                {r.count !== undefined && <span className="side-count">{fa(r.count)}</span>}
              </NavLink>
            ),
          )}
        </div>
      ))}
    </aside>
  )
}

function fa(n: number): string {
  return String(n).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
}
